import {
  addMemberInputSchema,
  MEMBERSHIP_ROLES,
  roleAtLeast,
  type AddMemberInput,
  type Member,
  type MembershipRole,
} from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { addMember, fetchMembers, removeMember, updateMemberRole } from '../api/workspace.api.js'
import { ROLE_LABELS } from '../auth/require-auth.js'
import { useCurrentUser } from '../auth/session-context.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { ConfirmDialog } from '../components/confirm-dialog.js'
import { DataTable, EmptyState, type Column } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field, SelectField } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { formatDateTime } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

export function MembersPage() {
  const workspace = useCurrentWorkspace()
  const currentUser = useCurrentUser()
  const queryClient = useQueryClient()

  const canManage = roleAtLeast(workspace.role, 'admin')
  const [toRemove, setToRemove] = useState<Member | null>(null)

  const members = useQuery({
    queryKey: ['members', workspace.id],
    queryFn: () => fetchMembers(workspace.id),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['members', workspace.id] })

  const changeRole = useMutation({
    mutationFn: (input: { membershipId: string; role: MembershipRole }) =>
      updateMemberRole(workspace.id, input.membershipId, input.role),
    onSuccess: () => void invalidate(),
  })

  const remove = useMutation({
    mutationFn: (membershipId: string) => removeMember(workspace.id, membershipId),
    onSuccess: () => {
      setToRemove(null)
      void invalidate()
      /**
       * Удалить можно и себя. Тогда сессия на сервене уже отозвана, и
       * список сессий надо перечитать — иначе интерфейс продолжит
       * показывать доступ, которого больше нет.
       */
      void queryClient.invalidateQueries({ queryKey: ['session'] })
    },
  })

  const columns: Column<Member>[] = [
    {
      key: 'user',
      header: 'Участник',
      render: (member) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{member.name ?? member.email}</p>
          {member.name && <p className="truncate text-xs text-muted">{member.email}</p>}
          {member.userId === currentUser.id && (
            <Badge className="mt-1" tone="neutral">
              это вы
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Роль',
      render: (member) =>
        canManage ? (
          <select
            aria-label={`Роль участника ${member.email}`}
            className="h-8 rounded-md border border-line bg-raised px-2 text-sm"
            value={member.role}
            disabled={changeRole.isPending}
            onChange={(event) =>
              changeRole.mutate({
                membershipId: member.membershipId,
                role: event.target.value as MembershipRole,
              })
            }
          >
            {MEMBERSHIP_ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role]}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm">{ROLE_LABELS[member.role]}</span>
        ),
    },
    {
      key: 'status',
      header: 'Доступ',
      render: (member) =>
        member.isActive ? (
          <Badge tone="ok">активен</Badge>
        ) : (
          <Badge tone="bad">заблокирован</Badge>
        ),
    },
    {
      key: 'createdAt',
      header: 'Добавлен',
      align: 'right',
      render: (member) => <span className="text-sm text-muted">{formatDateTime(member.createdAt)}</span>,
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            render: (member: Member) => (
              <Button variant="ghost" size="sm" onClick={() => setToRemove(member)}>
                Убрать
              </Button>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <PageHeader
        title="Участники"
        description="Кто имеет доступ к воркспейсу и с какими правами. Оператор не видит ключей интеграций."
      />

      <div className="flex flex-col gap-6">
        {changeRole.error && <ErrorPanel error={changeRole.error} />}

        <DataTable
          rows={members.data ?? []}
          columns={columns}
          rowKey={(member) => member.membershipId}
          isLoading={members.isPending}
          isRefreshing={members.isFetching && !members.isPending}
          empty={<EmptyState title="Участников пока нет" />}
        />

        {members.error && <ErrorPanel error={members.error} onRetry={() => void members.refetch()} />}

        {canManage && <AddMemberForm workspaceId={workspace.id} onAdded={invalidate} />}
      </div>

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title="Убрать участника из воркспейса?"
        description={
          <>
            {toRemove?.email} потеряет доступ немедленно: все его сессии будут отозваны. Сам
            пользователь и его записи в журнале действий сохранятся.
          </>
        }
        confirmLabel="Убрать"
        danger
        loading={remove.isPending}
        onConfirm={() => toRemove && remove.mutate(toRemove.membershipId)}
      />
    </>
  )
}

/**
 * Добавление участника.
 *
 * Поле пароля здесь — не пароль администратора, а начальный пароль
 * нового пользователя, и подпись это прямо говорит. Если пользователь
 * с таким адресом уже есть, пароль игнорируется: человеку выдаётся доступ
 * к воркспейсу, а не заводится вторая учётная запись.
 */
function AddMemberForm({
  workspaceId,
  onAdded,
}: {
  workspaceId: string
  onAdded: () => Promise<unknown>
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<MembershipRole>('operator')
  const [formError, setFormError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: (input: AddMemberInput) => addMember(workspaceId, input),
    onSuccess: async () => {
      setEmail('')
      setName('')
      setPassword('')
      setFormError(null)
      await onAdded()
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()

    const parsed = addMemberInputSchema.safeParse({
      email,
      role,
      ...(name ? { name } : {}),
      ...(password ? { password } : {}),
    })

    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Проверьте заполненные поля')
      return
    }

    setFormError(null)
    add.mutate(parsed.data)
  }

  return (
    <Panel title="Добавить участника" description="Если пользователя ещё нет, он будет создан">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Рабочий адрес"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label="Имя"
            hint="Необязательно"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Field
            label="Начальный пароль"
            type="password"
            autoComplete="new-password"
            hint="Нужен только для нового пользователя, минимум 10 символов"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={formError}
          />
          <SelectField
            label="Роль"
            value={role}
            onChange={(event) => setRole(event.target.value as MembershipRole)}
          >
            {MEMBERSHIP_ROLES.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value]}
              </option>
            ))}
          </SelectField>
        </div>

        {add.error && <ErrorPanel error={add.error} />}

        <div>
          <Button type="submit" variant="primary" loading={add.isPending}>
            Добавить
          </Button>
        </div>
      </form>
    </Panel>
  )
}
